import { watch, ref, onUnmounted } from 'vue';
import { useStore } from 'vuex';
import { useIqRecommendations } from './useIqRecommendations';
import { useRecommendationManager } from './useRecommendationManager';

export function useAutoIqRecommendations(currentChat) {
  const store = useStore();
  const { startIqRecommendationStream, stopIqRecommendationStream } =
    useIqRecommendations(currentChat);
  const { checkExistingRecommendations } = useRecommendationManager(store);
  const isProcessing = ref(false);
  
  let isDebouncing = false;
  let lastProcessedMessageId = null;
  
  const processRecommendations = async (messageId) => {
    if (isDebouncing) return;
    
    isProcessing.value = true;
    isDebouncing = true;
    
    try {
      stopIqRecommendationStream();
      
      setTimeout(() => {
        if (currentChat.value?.id) {
          startIqRecommendationStream(messageId);
        }
      }, 300);
    } catch (error) {
      console.error('Error processing recommendations:', error);
    } finally {
      setTimeout(() => {
        isProcessing.value = false;
        isDebouncing = false;
      }, 300);
    }
  };

  const stopWatcher = watch(
    () => currentChat.value?.messages,
    async newMessages => {
      if (isDebouncing || !currentChat.value?.id || !newMessages || !newMessages.length) return;

      const lastMessage = newMessages[newMessages.length - 1];
      const isContact = lastMessage?.sender?.type === "contact";
      const { exists, messageId } = await checkExistingRecommendations(currentChat.value.id, lastMessage.id, isContact);
      
      if (exists) {
        lastProcessedMessageId = messageId || lastMessage?.id;
        return;
      } else {
        const history = lastMessage?.custom_attributes?.history;
        const isNewMessage = lastMessage && lastMessage.id !== lastProcessedMessageId;
        const shouldFetchIq = history ? isContact && history.isLastMessage : isContact && isNewMessage;
        if(shouldFetchIq) {
          lastProcessedMessageId = lastMessage.id;
          processRecommendations(lastMessage.id);
          return;
        }
      }
    },
    { immediate: true, deep: true }
  );

  onUnmounted(() => {
    stopWatcher();
    stopIqRecommendationStream();
  });

  return {
    isProcessing,
  };
}
